import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2,
  Search,
  Clock,
  CheckCircle2,
  XCircle,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Edit3,
  Infinity as InfinityIcon,
  X,
} from "lucide-react";
import {
  getTimeline,
  setCitationTemporalAnchor,
} from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

/** v20.12 · Horizontal timeline that replaces the 3D node cloud.
 *  Each sealed citation drops at the year the belief was dominant
 *  (inferred by the AI panel during triangulation). The chain is
 *  doubly-linked (prev_id ↔ next_id) so an AI reader walking the
 *  ledger never has to jump across the DB. */

const TONE = {
  fact: { chip: "bg-emerald-500/10 border-emerald-500/40 text-emerald-300", dot: "bg-emerald-400 border-emerald-500" },
  refuted: { chip: "bg-rose-500/10 border-rose-500/40 text-rose-300", dot: "bg-rose-400 border-rose-500" },
  contested: {
    chip: "bg-gradient-to-r from-amber-500/15 to-rose-500/15 border border-amber-500/40 text-amber-200",
    dot: "bg-gradient-to-br from-amber-400 to-rose-400 border-amber-500",
  },
};

function toneOf(node) {
  if (node.contested_by_ai_panel) return TONE.contested;
  if (node.epistemic_status === "refuted") return TONE.refuted;
  return TONE.fact;
}

function formatYear(y) {
  if (y === null || y === undefined) return "—";
  if (y < 0) return `${Math.abs(y)} BCE`;
  if (y <= 1000) return `${y} CE`;
  return String(y);
}

export default function Timeline() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedEra, setSelectedEra] = useState(null);
  const [query, setQuery] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [selected, setSelected] = useState(null);
  const trackRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  const load = React.useCallback(() => {
    setLoading(true);
    getTimeline({
      limit: 500,
      era: selectedEra || undefined,
      q: debouncedQ || undefined,
    })
      .then(setData)
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Timeline load failed.");
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [selectedEra, debouncedQ]);

  useEffect(() => {
    load();
  }, [load]);

  const scale = useMemo(() => {
    if (!data || !data.nodes?.length) return null;
    const min = data.min_year ?? 0;
    const max = data.max_year ?? min + 1;
    const span = Math.max(1, max - min);
    return { min, max, span };
  }, [data]);

  const positionPct = (year) => {
    if (!scale) return 0;
    return ((year - scale.min) / scale.span) * 100;
  };

  const scrollToNeighbour = (dir) => {
    if (!selected || !data) return;
    const nextId = dir === "next" ? selected.next_id : selected.prev_id;
    if (!nextId) return;
    const nextNode = data.nodes.find((n) => n.id === nextId);
    if (nextNode) setSelected(nextNode);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5">
      <header className="space-y-2">
        <div className="flex items-center gap-3 flex-wrap">
          <Clock className="h-5 w-5 text-amber-400" />
          <h1 className="font-heading text-lg tracking-[0.25em] text-zinc-100">
            TRUTH TIMELINE
          </h1>
          <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
            {data?.count ?? 0} nodes · chain-walkable (prev ⇄ next)
          </span>
          <button
            type="button"
            onClick={load}
            className="ml-auto inline-flex items-center gap-1 px-2 py-1 border border-zinc-800 text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-amber-300 hover:border-amber-500/40 font-mono"
            data-testid="timeline-refresh"
          >
            <RefreshCw className="h-3 w-3" /> refresh
          </button>
        </div>
        <p className="text-[12px] text-zinc-500 max-w-2xl leading-snug">
          Every sealed citation anchored at the year its belief was dominant.
          Evolution of perception shows left-to-right — ancient beliefs on the
          left, contemporary on the right, timeless truths as bars that span
          the whole line.
        </p>
      </header>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Jump to any fact — type a word from the claim…"
          data-testid="timeline-search"
          className="w-full pl-9 pr-3 py-2 bg-transparent border border-zinc-800 text-[13px] font-mono text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50"
        />
      </div>

      {/* Era chips */}
      {data?.eras && (
        <div className="flex flex-wrap gap-2" data-testid="timeline-era-chips">
          <button
            type="button"
            onClick={() => setSelectedEra(null)}
            data-testid="timeline-era-all"
            className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.25em] font-mono border transition-colors ${
              !selectedEra
                ? "border-amber-500/50 text-amber-300 bg-amber-500/5"
                : "border-zinc-800 text-zinc-500 hover:text-amber-300 hover:border-amber-500/30"
            }`}
          >
            all · {data.count}
          </button>
          {data.eras.map((e) => {
            const count = data.era_counts?.[e.key] || 0;
            const active = selectedEra === e.key;
            return (
              <button
                key={e.key}
                type="button"
                onClick={() => setSelectedEra(active ? null : e.key)}
                data-testid={`timeline-era-${e.key}`}
                className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.25em] font-mono border transition-colors ${
                  active
                    ? "border-amber-500/50 text-amber-300 bg-amber-500/5"
                    : "border-zinc-800 text-zinc-500 hover:text-amber-300 hover:border-amber-500/30"
                }`}
                disabled={count === 0 && !active}
              >
                {e.label} · {count}
              </button>
            );
          })}
        </div>
      )}

      {/* Timeline track */}
      <div
        ref={trackRef}
        className="relative border border-zinc-800 bg-[#0b0b0d] overflow-x-auto"
        style={{ minHeight: 220 }}
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 z-10 text-zinc-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> loading…
          </div>
        )}
        {!loading && (!data || data.nodes.length === 0) && (
          <div className="p-8 text-sm text-zinc-500">
            No citations in this view yet.
          </div>
        )}
        {scale && data?.nodes.length > 0 && (
          <div className="relative" style={{ width: "100%", minWidth: 760 }}>
            {/* axis labels */}
            <div className="flex justify-between px-4 py-3 text-[10px] uppercase tracking-[0.25em] font-mono text-zinc-600 border-b border-zinc-900">
              <span>{formatYear(scale.min)}</span>
              <span>evolution of perception →</span>
              <span>{formatYear(scale.max)}</span>
            </div>
            {/* track line */}
            <div className="relative mx-6 my-10 h-[2px] bg-gradient-to-r from-zinc-800 via-amber-500/40 to-zinc-800">
              {data.nodes.map((n) => {
                const anchor = n.temporal_anchor || {};
                const tone = toneOf(n);
                const isSelected = selected?.id === n.id;
                if (anchor.timeless) {
                  // Timeless → render as a thin bar spanning the whole track
                  return (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => setSelected(n)}
                      data-testid={`timeline-node-${n.id}`}
                      className={`absolute left-0 right-0 h-[6px] -top-[2px] transition-opacity ${
                        isSelected ? "opacity-80" : "opacity-25 hover:opacity-60"
                      } ${
                        n.contested_by_ai_panel
                          ? "bg-gradient-to-r from-amber-400/60 to-rose-400/60"
                          : n.epistemic_status === "refuted"
                          ? "bg-rose-400/50"
                          : "bg-emerald-400/50"
                      }`}
                      title={`timeless · ${n.claim.slice(0, 60)}`}
                    />
                  );
                }
                const left = positionPct(n.position_year);
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => setSelected(n)}
                    data-testid={`timeline-node-${n.id}`}
                    style={{ left: `${left}%` }}
                    className={`absolute -translate-x-1/2 -top-2 h-4 w-4 border-2 ${
                      tone.dot
                    } ${
                      isSelected
                        ? "ring-2 ring-amber-500/60 scale-125"
                        : "hover:scale-110"
                    } transition-transform`}
                    title={`${formatYear(n.position_year)} · ${n.claim.slice(0, 60)}`}
                  />
                );
              })}
            </div>
            {/* chronological row below the axis — fallback list for mobile / accessibility */}
            <div className="px-4 pb-4 space-y-1 max-h-[220px] overflow-y-auto">
              {data.nodes.map((n) => {
                const tone = toneOf(n);
                return (
                  <button
                    key={`row-${n.id}`}
                    type="button"
                    onClick={() => setSelected(n)}
                    data-testid={`timeline-row-${n.id}`}
                    className={`w-full text-left flex items-center gap-2 px-2 py-1 border-l-2 ${
                      selected?.id === n.id
                        ? "border-amber-500 bg-amber-500/5"
                        : "border-transparent hover:bg-zinc-900/40"
                    }`}
                  >
                    <span className={`shrink-0 w-20 text-[10px] font-mono uppercase tracking-[0.25em] px-1 py-0.5 border ${tone.chip}`}>
                      {n.temporal_anchor?.timeless
                        ? "timeless"
                        : formatYear(n.position_year)}
                    </span>
                    <span className="text-[12px] text-zinc-300 truncate">
                      {n.claim}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {selected && (
        <aside
          data-testid="timeline-detail-drawer"
          className="fixed right-4 top-20 w-[min(92vw,420px)] max-h-[80vh] overflow-y-auto border border-amber-500/40 bg-[#0b0b0d] shadow-2xl z-30"
        >
          <div className="flex items-center gap-2 border-b border-zinc-900 px-3 py-2">
            <span className="text-[10px] uppercase tracking-[0.25em] font-mono text-amber-300">
              {selected.contested_by_ai_panel
                ? "contested"
                : selected.epistemic_status || "fact"}
            </span>
            <span className="text-[10px] uppercase tracking-[0.25em] font-mono text-zinc-500 ml-auto">
              {selected.temporal_anchor?.timeless ? (
                <span className="inline-flex items-center gap-1 text-amber-300">
                  <InfinityIcon className="h-3 w-3" /> timeless
                </span>
              ) : (
                `${formatYear(selected.temporal_anchor?.start_year)} → ${
                  selected.temporal_anchor?.end_year
                    ? formatYear(selected.temporal_anchor.end_year)
                    : "today"
                }`
              )}
            </span>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="text-zinc-500 hover:text-amber-300"
              data-testid="timeline-detail-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="p-3 space-y-3">
            <div className="text-sm text-zinc-100 leading-relaxed">
              {selected.claim}
            </div>
            {selected.temporal_anchor?.note && (
              <div className="text-[11px] font-mono text-zinc-500 border-l-2 border-amber-500/30 pl-2">
                {selected.temporal_anchor.note}
              </div>
            )}
            <div className="flex items-center gap-2 pt-1 border-t border-zinc-900">
              <button
                type="button"
                onClick={() => scrollToNeighbour("prev")}
                disabled={!selected.prev_id}
                data-testid="timeline-detail-prev"
                className="inline-flex items-center gap-1 px-2 py-1 border border-zinc-800 text-[10px] uppercase tracking-[0.25em] font-mono text-zinc-400 hover:text-amber-300 hover:border-amber-500/40 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-3 w-3" /> prev
              </button>
              <button
                type="button"
                onClick={() => scrollToNeighbour("next")}
                disabled={!selected.next_id}
                data-testid="timeline-detail-next"
                className="inline-flex items-center gap-1 px-2 py-1 border border-zinc-800 text-[10px] uppercase tracking-[0.25em] font-mono text-zinc-400 hover:text-amber-300 hover:border-amber-500/40 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                next <ChevronRight className="h-3 w-3" />
              </button>
              <span className="ml-auto text-[9px] font-mono text-zinc-600">
                src: {selected.temporal_anchor?.source || "legacy"}
              </span>
            </div>
            {user?.is_architect && (
              <ArchitectAnchorEditor
                node={selected}
                onSaved={(anchor) => {
                  setSelected((s) =>
                    s ? { ...s, temporal_anchor: anchor } : s
                  );
                  load();
                }}
              />
            )}
          </div>
        </aside>
      )}
    </div>
  );
}

function ArchitectAnchorEditor({ node, onSaved }) {
  const anchor = node.temporal_anchor || {};
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(
    anchor.start_year == null ? "" : String(anchor.start_year)
  );
  const [end, setEnd] = useState(
    anchor.end_year == null ? "" : String(anchor.end_year)
  );
  const [timeless, setTimeless] = useState(Boolean(anchor.timeless));
  const [note, setNote] = useState(anchor.note || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await setCitationTemporalAnchor(node.id, {
        start_year: start === "" ? null : parseInt(start, 10),
        end_year: end === "" ? null : parseInt(end, 10),
        timeless,
        note,
      });
      toast.success("Anchor updated");
      onSaved?.(res.temporal_anchor);
      setOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`timeline-anchor-edit-${node.id}`}
        className="inline-flex items-center gap-1 px-2 py-1 border border-zinc-800 text-[10px] uppercase tracking-[0.25em] font-mono text-zinc-400 hover:text-amber-300 hover:border-amber-500/40"
      >
        <Edit3 className="h-3 w-3" /> edit anchor
      </button>
    );
  }

  return (
    <div className="space-y-2 border border-amber-500/30 bg-amber-500/[0.03] p-3">
      <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-amber-300">
        edit anchor (architect)
      </div>
      <label className="flex items-center gap-2 text-[11px] font-mono text-zinc-300">
        <input
          type="checkbox"
          checked={timeless}
          onChange={(e) => setTimeless(e.target.checked)}
          data-testid={`timeline-anchor-timeless-${node.id}`}
        />
        timeless (still correct today)
      </label>
      <div className="flex gap-2">
        <label className="flex-1 text-[10px] uppercase tracking-[0.25em] font-mono text-zinc-500">
          start year (neg = BCE)
          <input
            type="number"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            placeholder="e.g. -500"
            data-testid={`timeline-anchor-start-${node.id}`}
            className="mt-1 w-full px-2 py-1 bg-transparent border border-zinc-800 text-[12px] font-mono text-zinc-100 focus:outline-none focus:border-amber-500/50"
          />
        </label>
        <label className="flex-1 text-[10px] uppercase tracking-[0.25em] font-mono text-zinc-500">
          end year
          <input
            type="number"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            placeholder="blank = today"
            data-testid={`timeline-anchor-end-${node.id}`}
            className="mt-1 w-full px-2 py-1 bg-transparent border border-zinc-800 text-[12px] font-mono text-zinc-100 focus:outline-none focus:border-amber-500/50"
          />
        </label>
      </div>
      <input
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value.slice(0, 120))}
        placeholder="context note (max 120 chars)"
        data-testid={`timeline-anchor-note-${node.id}`}
        className="w-full px-2 py-1 bg-transparent border border-zinc-800 text-[12px] font-mono text-zinc-100 focus:outline-none focus:border-amber-500/50"
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid={`timeline-anchor-save-${node.id}`}
          className="inline-flex items-center gap-1 px-2 py-1 border border-amber-500/50 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 text-[10px] uppercase tracking-[0.25em] font-mono disabled:opacity-40"
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : null} save
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          disabled={saving}
          className="text-[10px] uppercase tracking-[0.25em] font-mono text-zinc-500 hover:text-zinc-300"
        >
          cancel
        </button>
      </div>
    </div>
  );
}
